import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, CreditCard } from "lucide-react";
import { api } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [bookingId, setBookingId] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (tries.current >= 8) { setState("timeout"); return; }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setBookingId(data.booking_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed") { setState("error"); return; }
        setTimeout(poll, 1800);
      } catch { setState("error"); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="w-container py-16 grid place-items-center" data-testid="payment-success-page">
      <div className="w-card p-8 max-w-md w-full text-center">
        {state === "checking" && (<>
          <Loader2 size={48} className="mx-auto text-mint-ink animate-spin" aria-hidden="true" />
          <h1 className="text-2xl font-extrabold mt-4">Zahlung wird bestätigt…</h1>
          <p className="w-muted mt-2">Einen Moment, wir prüfen deine Zahlung.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={56} className="mx-auto text-[var(--w-mint-ink)]" aria-hidden="true" />
          <h1 className="text-2xl font-extrabold mt-4" data-testid="payment-paid-title">Zahlung erfolgreich!</h1>
          <p className="w-muted mt-2">Danke – dein Waschheld wurde informiert und holt deine Wäsche wie vereinbart ab.</p>
          <Link to={bookingId ? `/bookings/${bookingId}` : "/bookings"} className="w-btn w-btn-primary mt-6" data-testid="payment-to-booking">Zur Buchung</Link>
        </>)}
        {state === "timeout" && (<>
          <CreditCard size={48} className="mx-auto text-mint-ink" aria-hidden="true" />
          <h1 className="text-2xl font-extrabold mt-4">Fast geschafft</h1>
          <p className="w-muted mt-2">Deine Zahlung wird noch verarbeitet. Das kann einen Moment dauern – prüfe gleich den Status in deiner Buchung.</p>
          <Link to={bookingId ? `/bookings/${bookingId}` : "/bookings"} className="w-btn w-btn-primary mt-6">Zur Buchung</Link>
        </>)}
        {state === "error" && (<>
          <XCircle size={48} className="mx-auto text-[var(--w-error)]" aria-hidden="true" />
          <h1 className="text-2xl font-extrabold mt-4">Etwas ist schiefgelaufen</h1>
          <p className="w-muted mt-2">Wir konnten die Zahlung nicht bestätigen. Falls Geld abgebucht wurde, melde dich über dein Postfach.</p>
          <Link to="/bookings" className="w-btn w-btn-secondary mt-6">Zu meinen Buchungen</Link>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking");
  return (
    <div className="w-container py-16 grid place-items-center" data-testid="payment-cancel-page">
      <div className="w-card p-8 max-w-md w-full text-center">
        <XCircle size={48} className="mx-auto text-[var(--w-muted)]" aria-hidden="true" />
        <h1 className="text-2xl font-extrabold mt-4">Zahlung abgebrochen</h1>
        <p className="w-muted mt-2">Kein Problem – es wurde nichts abgebucht. Du kannst die Zahlung jederzeit erneut starten.</p>
        <Link to={bookingId ? `/bookings/${bookingId}` : "/bookings"} className="w-btn w-btn-primary mt-6" data-testid="cancel-to-booking">Zurück zur Buchung</Link>
      </div>
    </div>
  );
}
